/**
 * Riders Report Page
 * 
 * Admin report showing all active student riders with filtering and export capabilities
 * - Filter by grade, group, and category
 * - Summary statistics
 * - Sortable table with contact information
 * - Export to CSV
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { restClient, Rider, Group, User } from '../../api/rest-client';
import { Button } from '../../components/common/Button';
import { formatRiderName } from '../../utils/riderDisplay';

interface RiderData {
  name: string;
  type: string;
  category: string;
  grade: string;
  group: string;
  contacts: string;
}

const RidersReport: React.FC = () => {
  const navigate = useNavigate();
  const [riders, setRiders] = useState<RiderData[]>([]);
  const [filteredRiders, setFilteredRiders] = useState<RiderData[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSeason, setCurrentSeason] = useState<string>('');
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);

  // Filters
  const [seasonFilter, setSeasonFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Sort state
  const [sortColumn, setSortColumn] = useState<'name' | 'type' | 'category' | 'grade' | 'group'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadData();
  }, []);

  // Reload data when season filter changes
  useEffect(() => {
    if (seasonFilter && currentSeason) {
      loadData(seasonFilter);
    }
  }, [seasonFilter]);

  useEffect(() => {
    applyFilters();
  }, [riders, seasonFilter, gradeFilter, groupFilter, categoryFilter]);

  const loadData = async (season?: string) => {
    try {
      setLoading(true);
      setError(null);

      // Get current season
      const seasonConfig = await restClient.getSeasonConfig();
      const currentSeasonValue = seasonConfig.currentSeason;
      setCurrentSeason(currentSeasonValue);
      setAvailableSeasons(seasonConfig.availableSeasons);
      
      // Set default season filter to current season if not already set
      if (!seasonFilter && !season) {
        setSeasonFilter(currentSeasonValue);
      }

      // Get all riders (like legacy app)
      const allRiders = await restClient.getRiders();
      
      // Get users for contact lookup
      const usersData = await restClient.getUsers();
      
      // Filter for active student riders in selected season (or current season)
      // Accept both 'Student' and 'Rider' types (legacy uses 'Student', new uses 'Rider')
      // Check disabled field like legacy app
      // Check both primary season and seasons_active array (OR logic)
      const selectedSeason = season || seasonFilter || currentSeasonValue;
      const ridersData = allRiders.filter(
        (rider: Rider) => {
          const isStudent = rider.type === 'Student' || rider.type === 'Rider';
          const isActive = rider.disabled !== '1' && rider.disabled !== 1;
          // Check if rider is active in selected season
          // Either primary season matches OR seasons_active contains selected season
          const isInSeason = rider.season === selectedSeason || 
            (rider.seasons_active && rider.seasons_active.includes(selectedSeason));
          return isStudent && isActive && isInSeason;
        }
      );

      // Transform to rider data format
      const transformedRiders: RiderData[] = ridersData.map((rider: Rider) => ({
        name: formatRiderName(rider),
        type: rider.type || 'Student',
        category: rider.category || '',
        grade: rider.grade || '',
        group: rider.group || '',
        contacts: formatContacts(rider.associatedUsers, usersData),
      }));

      // Sort by name initially
      transformedRiders.sort((a, b) => a.name.localeCompare(b.name));

      setRiders(transformedRiders);

      // Load groups for filter
      const groupsData = await restClient.getGroups();
      setGroups(groupsData);
    } catch (err) {
      setError('Failed to load riders data');
    } finally {
      setLoading(false);
    }
  };

  const formatContacts = (associatedUsersJson: string | undefined, availableUsers: User[]): string => {
    if (!associatedUsersJson) return '';

    try {
      const users = JSON.parse(associatedUsersJson);
      if (!users || users.length === 0) return '';

      return users
        .map((user: any) => {
          const userDetails = availableUsers.find((u) => u.email === user.user_id || u.username === user.user_id);
          const userName = userDetails ? userDetails.name || userDetails.email : 'Unknown User';
          const userEmail = userDetails ? userDetails.email : user.user_id;

          return `${userName} (${userEmail}) - ${user.role}`;
        })
        .join('; ');
    } catch (e) {
      return '';
    }
  };

  const applyFilters = () => {
    let filtered = [...riders];

    // Grade filter
    if (gradeFilter) {
      filtered = filtered.filter((rider) => rider.grade === gradeFilter);
    }

    // Group filter
    if (groupFilter) {
      filtered = filtered.filter((rider) => rider.group === groupFilter);
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered.filter((rider) => rider.category === categoryFilter);
    }

    setFilteredRiders(filtered);
  };

  const clearFilters = useCallback(() => {
    setSeasonFilter(currentSeason); // Reset to current season
    setGradeFilter('');
    setGroupFilter('');
    setCategoryFilter('');
  }, [currentSeason]);

  const handleSort = useCallback((column: 'name' | 'type' | 'category' | 'grade' | 'group') => {
    const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDirection);

    const sorted = [...filteredRiders].sort((a, b) => {
      const aVal = a[column] || '';
      const bVal = b[column] || '';

      // For grade, use custom ordering
      if (column === 'grade') {
        const gradeOrder = ['6th', '7th', '8th', '9th', '10th', '11th', '12th'];
        const aIndex = gradeOrder.indexOf(aVal);
        const bIndex = gradeOrder.indexOf(bVal);
        if (aIndex !== -1 && bIndex !== -1) {
          return newDirection === 'asc' ? aIndex - bIndex : bIndex - aIndex;
        }
      }

      // For category, use custom ordering
      if (column === 'category') {
        const categoryOrder = ['Varsity', 'JV1', 'JV2', 'Freshman', '8th Grade', '7th Grade', '6th Grade'];
        const aIndex = categoryOrder.indexOf(aVal);
        const bIndex = categoryOrder.indexOf(bVal);
        if (aIndex !== -1 && bIndex !== -1) {
          return newDirection === 'asc' ? aIndex - bIndex : bIndex - aIndex;
        }
      }

      // Handle null/undefined values for localeCompare
      const aString = String(aVal || '');
      const bString = String(bVal || '');
      return newDirection === 'asc' ? aString.localeCompare(bString) : bString.localeCompare(aString);
    });

    setFilteredRiders(sorted);
  }, [sortColumn, sortDirection, filteredRiders]);

  const exportToCSV = useCallback(() => {
    if (filteredRiders.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Name', 'Type', 'Category', 'Grade', 'Group', 'Contacts'];
    const csvContent = [
      headers.join(','),
      ...filteredRiders.map((rider) =>
        [
          `"${rider.name}"`,
          `"${rider.type}"`,
          `"${rider.category}"`,
          `"${rider.grade}"`,
          `"${rider.group}"`,
          `"${rider.contacts}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `riders-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredRiders]);

  // Calculate statistics
  const getStatistics = () => {
    const groupCounts: Record<string, number> = {};
    const gradeCounts: Record<string, number> = {};
    const categoryCounts: Record<string, number> = {};

    filteredRiders.forEach((rider) => {
      const group = rider.group || 'No Group';
      groupCounts[group] = (groupCounts[group] || 0) + 1;

      const grade = rider.grade || 'No Grade';
      gradeCounts[grade] = (gradeCounts[grade] || 0) + 1;

      const category = rider.category || 'No Category';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    return { groupCounts, gradeCounts, categoryCounts };
  };

  const { groupCounts, gradeCounts, categoryCounts } = getStatistics();

  // Get unique values for filters
  const uniqueGrades = [...new Set(riders.map((r) => r.grade).filter((g) => g))].sort((a, b) => {
    const gradeOrder = ['6th', '7th', '8th', '9th', '10th', '11th', '12th'];
    return gradeOrder.indexOf(a) - gradeOrder.indexOf(b);
  });

  const uniqueCategories = [...new Set(riders.map((r) => r.category).filter((c) => c))].sort((a, b) => {
    const categoryOrder = ['Varsity', 'JV1', 'JV2', 'Freshman', '8th Grade', '7th Grade', '6th Grade'];
    return categoryOrder.indexOf(a) - categoryOrder.indexOf(b);
  });

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading riders data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Riders Report</h1>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate('/admin/reports')}
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Reports
            </Button>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">Report Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <strong>Total Riders:</strong> {riders.length}
            </div>
            <div>
              <strong>Filtered Results:</strong> {filteredRiders.length}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <strong>Riders by Group:</strong>
              <div className="text-sm mt-1">
                {Object.entries(groupCounts).length > 0 ? (
                  Object.entries(groupCounts).map(([group, count]) => (
                    <div key={group}>
                      {group}: {count}
                    </div>
                  ))
                ) : (
                  <div>None</div>
                )}
              </div>
            </div>
            <div>
              <strong>Riders by Grade:</strong>
              <div className="text-sm mt-1">
                {Object.entries(gradeCounts)
                  .sort(([a], [b]) => {
                    const gradeOrder = ['6th', '7th', '8th', '9th', '10th', '11th', '12th', 'No Grade'];
                    return gradeOrder.indexOf(a) - gradeOrder.indexOf(b);
                  })
                  .map(([grade, count]) => (
                    <div key={grade}>
                      {grade}: {count}
                    </div>
                  ))}
              </div>
            </div>
            <div>
              <strong>Riders by Category:</strong>
              <div className="text-sm mt-1">
                {Object.entries(categoryCounts)
                  .sort(([a], [b]) => {
                    const categoryOrder = ['Varsity', 'JV1', 'JV2', 'Freshman', '8th Grade', '7th Grade', '6th Grade', 'No Category'];
                    return categoryOrder.indexOf(a) - categoryOrder.indexOf(b);
                  })
                  .map(([category, count]) => (
                    <div key={category}>
                      {category}: {count}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block font-semibold mb-2">Filter by Season:</label>
              <select
                value={seasonFilter}
                onChange={(e) => setSeasonFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                {availableSeasons.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">Filter by Grade:</label>
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="">All Grades</option>
                {uniqueGrades.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">Filter by Group:</label>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="">All Groups</option>
                {groups.map((group) => (
                  <option key={group.group_id} value={group.name}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">Filter by Category:</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="">All Categories</option>
                {uniqueCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">Actions:</label>
              <button
                onClick={clearFilters}
                className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th
                  className="border border-gray-300 px-4 py-2 text-left cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('name')}
                >
                  Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border border-gray-300 px-4 py-2 text-left cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('type')}
                >
                  Type {sortColumn === 'type' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border border-gray-300 px-4 py-2 text-left cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('category')}
                >
                  Category {sortColumn === 'category' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border border-gray-300 px-4 py-2 text-left cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('grade')}
                >
                  Grade {sortColumn === 'grade' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border border-gray-300 px-4 py-2 text-left cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('group')}
                >
                  Group {sortColumn === 'group' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">Contacts</th>
              </tr>
            </thead>
            <tbody>
              {filteredRiders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="border border-gray-300 px-4 py-2 text-center">
                    No riders found
                  </td>
                </tr>
              ) : (
                filteredRiders.map((rider, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">{rider.name}</td>
                    <td className="border border-gray-300 px-4 py-2">{rider.type}</td>
                    <td className="border border-gray-300 px-4 py-2">{rider.category}</td>
                    <td className="border border-gray-300 px-4 py-2">{rider.grade}</td>
                    <td className="border border-gray-300 px-4 py-2">{rider.group}</td>
                    <td className="border border-gray-300 px-4 py-2 text-sm">
                      {rider.contacts ? (
                        <div className="space-y-1">
                          {rider.contacts.split('; ').map((contact, i) => (
                            <div key={i}>{contact}</div>
                          ))}
                        </div>
                      ) : (
                        ''
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RidersReport;
